import { useQuery } from '@tanstack/react-query';
import { supabaseClient } from '../utils/supabase';

export interface FundedSummary {
  amount_funded: number;
  deals_funded: number;
  total_commission: number;
  average_deal: number;
}

export function fundedSummaryQueryOptions() {
  return {
    queryKey: ['funded-summary-all-time'] as const,
    staleTime: 5 * 60_000,
    queryFn: async (): Promise<FundedSummary | null> => {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const { data, error } = await (supabaseClient.rpc as any)('rpt_funded_summary_fn', {});
      if (error) throw error;
      const row = Array.isArray(data) ? data[0] : data;
      if (!row) return null;
      return {
        amount_funded: Number(row.amount_funded) || 0,
        deals_funded: Number(row.deals_funded) || 0,
        total_commission: Number(row.total_commission) || 0,
        average_deal: Number(row.average_deal) || 0,
      };
    },
  };
}

/**
 * Canonical all-time funded totals — rpt_funded_summary_fn over
 * rpt_funded_deals_v: UNIQUE funded submissions (status Funded, real
 * date_funded, clawbacks/bounced/cancelled/default excluded). The same
 * numbers as the Funded dashboard's KPI cards, so every list-page header
 * agrees with it instead of summing per-entity rows (which double-counts a
 * submission once per linked rep and drifts on looser per-entity views).
 *
 * RBAC: the report is capability-gated; a user without `funded_summary`
 * access gets zeros back — callers should render a dash while loading and
 * treat all-zero as "not visible to you".
 */
export function useFundedSummary() {
  return useQuery({
    ...fundedSummaryQueryOptions(),
  });
}
